<template>
    <div>
        <Suggestions :suggestions="suggestions" :colors="colors" @send-suggestion="_submitSuggestion" />

        <form :class="{ active: inputActive }" :style="inputBackground" class="sc-user-input">
            <div
                ref="userInput"
                :placeholder="adjustedPlaceholder"
                :style="{ color: colors.userInput.text }"
                disabled
                :contentEditable="!disabled"
                role="button"
                tabIndex="0"
                class="sc-user-input--text"
                @focus="setInputActive(true)"
                @blur="setInputActive(false)"
                @keydown="handleKey"
            />
            <div class="sc-user-input--buttons">
                <div v-if="showEmoji" class="sc-user-input--button">
                    <EmojiIcon :on-emoji-picked="_handleEmojiPicked" :color="colors.userInput.text" />
                </div>
                <div v-if="showFile" class="sc-user-input--button">
                    <FileIcons :on-change="_handleFileSubmit" :color="colors.userInput.text" />
                </div>
                <div class="sc-user-input--button">
                    <SendIcon :on-click="_submitText" :color="colors.userInput.text" />
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import Color from 'color';
import { createNamespacedHelpers } from 'vuex';

import EmojiIcon from './EmojiIcon.vue';
import FileIcons from './FileIcons.vue';
import SendIcon from './SendIcon.vue';
import Suggestions from './Suggestions.vue';

const { mapState } = createNamespacedHelpers('Chat');

export default {
    components: { EmojiIcon, FileIcons, SendIcon, Suggestions },

    props: {
        showEmoji: {
            type: Boolean,
            default: () => false,
        },
        suggestions: {
            type: Array,
            default: () => [],
        },
        showFile: {
            type: Boolean,
            default: () => false,
        },
        onSubmit: {
            type: Function,
            required: true,
        },
        placeholder: {
            type: String,
            default: 'Write a reply',
        },
        colors: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['typing'],

    data() {
        return {
            file: null,
            inputActive: false,
        };
    },

    computed: {
        ...mapState(['authUser']),

        adjustedPlaceholder() {
            return !this.disabled ? this.placeholder : '';
        },

        inputBackground() {
            const background = this.disabled ? Color(this.colors.userInput.bg).darken(0.05) : this.colors.userInput.bg;

            return { background };
        },
    },

    methods: {
        cancelFile() {
            this.file = null;
        },

        setInputActive(onoff) {
            if (this.disabled) {
                return;
            }

            this.inputActive = onoff;
        },

        handleKey(event) {
            if (event.keyCode === 13 && !event.shiftKey) {
                this._submitText(event);
                event.preventDefault();
            } else {
                this.$emit('typing');
            }
        },

        _submitSuggestion(suggestion) {
            this.onSubmit({
                author: this.authUser.id,
                type: 'text',
                data: { text: suggestion },
            });
        },

        _submitText() {
            const text = this.$refs.userInput.textContent;
            const { file } = this;
            if (file) {
                if (text && text.length > 0) {
                    this.onSubmit({
                        author: this.authUser.id,
                        type: 'file',
                        data: { text, file },
                    });
                    this.file = null;
                    this.$refs.userInput.innerHTML = '';
                } else {
                    this.onSubmit({
                        author: this.authUser.id,
                        type: 'file',
                        data: { file },
                    });
                    this.file = null;
                }
            } else if (text && text.length > 0) {
                this.onSubmit({
                    author: this.authUser.id,
                    type: 'text',
                    data: { text },
                });
                this.$refs.userInput.innerHTML = '';
            }
        },

        _handleEmojiPicked(emoji) {
            this.onSubmit({
                author: this.authUser.id,
                type: 'emoji',
                data: { emoji },
            });
        },

        _handleFileSubmit(file) {
            this.file = file;
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

.sc-user-input {
    align-items: center;
    min-height: 60px;
    margin: 0;
    position: relative;
    bottom: 0;
    display: flex;
    background-color: $grey-lighter;
    border-bottom-left-radius: $radius-large;
    border-bottom-right-radius: $radius-large;
    justify-content: space-between;
}

.sc-user-input--text {
    resize: none;
    border: none;
    outline: none;
    border-bottom-left-radius: $radius-large;
    box-sizing: border-box;
    padding: $column-gap;
    flex-grow: 1;
    font-size: $small-font-size;
    line-height: 1.33;
    white-space: pre-wrap;
    word-wrap: break-word;
    color: $text;
    max-height: 200px;
    overflow: scroll;
    bottom: 0;
    overflow-x: hidden;
    overflow-y: auto;
}

.sc-user-input--text:empty:before {
    content: attr(placeholder);
    display: block; /* For Firefox */
    /* color: rgba(86, 88, 103, 0.3); */
    filter: contrast(15%);
    outline: none;
}

.sc-user-input--buttons {
    display: flex;
    height: 100%;
}

.sc-user-input--button {
    align-items: center;
    height: 60px;
    display: flex;
}

.sc-user-input.active {
    background-color: white;
}

.sc-user-input--button label {
    position: relative;
    height: 24px;
    padding-left: 3px;
    cursor: pointer;
}

.sc-user-input--button label:hover path {
    fill: $grey-light;
}

.sc-user-input--button input {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    z-index: 99999;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    overflow: hidden;
}

.file-container {
    background-color: $grey-lighter;
    border-top-left-radius: $radius-large;
    padding: $column-gap;
    color: $text;
}

.delete-file-message {
    font-style: normal;
    float: right;
    cursor: pointer;
    color: #c8cad0;
}

.delete-file-message:hover {
    color: $text;
}

.icon-file-message {
    margin-right: 5px;
}
</style>
